import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Eye, Search, FileText, MessageCircle, AlertTriangle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { formatPrice, formatDate, formatDateTime, ORDER_STATUS, PAYMENT_STATUS } from "@/lib/format";
import { AdminModal, Pill } from "@/pages/admin/ui";
import WhatsAppLink from "@/components/WhatsAppLink";

export default function AdminVendorSales() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [proofUrl, setProofUrl] = useState("");

  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    params.set("page", String(page));
    api.get(`/admin/vendor-sales?${params}`).then(({ data: d }) => setData(d)).catch((e) => toast.error(apiError(e)));
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, status, page]);

  const openDetail = async (sale) => {
    setSelected(sale); setDetail(null); setProofUrl("");
    try {
      const { data: d } = await api.get(`/admin/vendor-sales/${sale.id}`);
      setDetail(d);
      if (d.payment_claim?.proof_path) {
        const { data: blob } = await api.get(`/admin/vendor-sales/${sale.id}/proof`, { responseType: "blob" });
        setProofUrl(URL.createObjectURL(blob));
      }
    } catch (e) { toast.error(apiError(e)); setSelected(null); }
  };

  const items = data?.items || [];

  return (
    <div className="space-y-5" data-testid="admin-vendor-sales">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-ink">Ventes des revendeurs</h1>
          <p className="mt-1 text-sm text-slate-500">
            Supervision en lecture seule — les revendeurs gèrent eux-mêmes leurs commandes et paiements.
          </p>
        </div>
        <span className="relative">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Commande, client, revendeur, TXID..."
            data-testid="vsales-search" className="h-10 w-64 rounded-lg border border-line pl-9 pr-3 text-sm focus:border-brand focus:outline-none" />
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        {[["", "Toutes"], ["payment_review", "Paiement en vérif."], ["processing", "En traitement"], ["delivered", "Livrées"], ["completed", "Terminées"], ["cancelled", "Annulées"]].map(([v, l]) => (
          <button key={v} onClick={() => { setStatus(v); setPage(1); }} data-testid={`vsales-filter-${v || "all"}`}
            className={`rounded-full px-4 py-2 text-xs font-bold ${status === v ? "bg-ink text-white" : "border border-line bg-white text-slate-600"}`}>{l}</button>
        ))}
      </div>

      {!data ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : !items.length ? (
        <div className="rounded-2xl border border-dashed border-line bg-white p-10 text-center text-sm text-slate-500">Aucune vente revendeur.</div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-2xl border border-line bg-white">
            <table className="w-full min-w-[960px] text-sm">
              <thead><tr className="border-b border-line text-left text-[11px] uppercase tracking-wide text-slate-400">
                <th className="p-4">Commande</th><th className="p-4">Revendeur</th><th className="p-4">Client</th>
                <th className="p-4">Contenu</th><th className="p-4">Paiement</th><th className="p-4">Montant</th>
                <th className="p-4">Statut</th><th className="p-4 text-right">Détail</th>
              </tr></thead>
              <tbody className="divide-y divide-line">
                {items.map((s) => {
                  const st = ORDER_STATUS[s.status] || {};
                  const ps = PAYMENT_STATUS[s.payment_status] || {};
                  return (
                    <tr key={s.id} data-testid={`vsale-${s.id}`}>
                      <td className="p-4"><span className="font-bold text-ink">{s.order_number}</span><br /><span className="text-xs text-slate-400">{formatDate(s.created_at)}</span></td>
                      <td className="p-4 font-semibold text-brand">{s.vendor_name}</td>
                      <td className="p-4"><span className="text-ink">{s.customer_name || "—"}</span><br /><span className="text-xs text-slate-400">{s.customer_email}</span></td>
                      <td className="max-w-[200px] p-4 text-xs text-slate-600">{s.items.map((i) => i.name).join(", ")}</td>
                      <td className="p-4 text-xs">
                        <span className="font-bold text-ink">{s.payment_method || "—"}</span>
                        {s.transaction_id && <><br /><span className="font-mono text-slate-500">{s.transaction_id}</span></>}
                      </td>
                      <td className="p-4 font-bold text-ink">{formatPrice(s.total)}</td>
                      <td className="p-4">
                        <span className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${st.className || "bg-slate-100 text-slate-600"}`}>{st.label || s.status}</span>
                        <br />
                        <span className={`mt-1 inline-block rounded-full border px-2.5 py-0.5 text-[10px] font-bold ${ps.className || "bg-slate-100 text-slate-600"}`}>{ps.label || s.payment_status}</span>
                      </td>
                      <td className="p-4 text-right">
                        <button onClick={() => openDetail(s)} data-testid={`vsale-view-${s.id}`}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-line text-slate-500 hover:border-brand hover:text-brand">
                          <Eye className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {data.pages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded-lg border border-line px-4 py-2 text-xs font-bold disabled:opacity-40">← Précédent</button>
              <span className="text-xs text-slate-500">Page {data.page} / {data.pages} · {data.total} ventes</span>
              <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="rounded-lg border border-line px-4 py-2 text-xs font-bold disabled:opacity-40">Suivant →</button>
            </div>
          )}
        </>
      )}

      <AdminModal open={!!selected} onOpenChange={() => setSelected(null)} title={`Vente ${selected?.order_number || ""}`} wide>
        {!detail ? (
          <div className="flex h-32 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs font-bold text-amber-800" data-testid="vsale-readonly-note">
              🔒 Lecture seule : cette vente est gérée par le revendeur. L'administrateur supervise et intervient uniquement en cas de problème.
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-xl border border-line p-4">
                <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">Revendeur</p>
                {(detail.vendors || []).map((v) => (
                  <div key={v.id} className="mb-2">
                    <p className="text-sm font-bold text-brand">{v.shop_name}</p>
                    <p className="text-xs text-slate-500">{v.email} · <WhatsAppLink phone={v.phone} /></p>
                    <div className="mt-1.5 flex gap-2">
                      {v.phone && (
                        <a href={`https://wa.me/${v.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer"
                          data-testid={`vsale-contact-${v.id}`}
                          className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1.5 text-[11px] font-bold text-white">
                          <MessageCircle className="h-3 w-3" /> Contacter (WhatsApp)
                        </a>
                      )}
                      <a href={`mailto:${v.email}`} data-testid={`vsale-email-${v.id}`} className="inline-flex items-center gap-1 rounded-full border border-line px-3 py-1.5 text-[11px] font-bold text-ink hover:border-brand">Email</a>
                    </div>
                  </div>
                ))}
              </div>
              <div className="rounded-xl border border-line p-4">
                <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">Client</p>
                <p className="text-sm font-bold text-ink">{detail.customer?.name || "—"}</p>
                <p className="text-xs text-slate-500">{detail.customer?.email} · <WhatsAppLink phone={detail.customer?.phone} /></p>
                <p className="text-xs text-slate-400">{detail.customer?.address || ""} {detail.customer?.country || ""}</p>
              </div>
            </div>

            <div className="rounded-xl border border-line p-4">
              <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">Produits / formations vendus</p>
              {detail.items.map((i, idx) => (
                <div key={idx} className="flex items-center justify-between py-1 text-sm">
                  <span className="text-ink">{i.name} × {i.quantity}
                    <Pill tone="blue" className="ml-2">{i.item_type === "formation" ? "Formation" : "Produit"}</Pill>
                    <span className="ml-1 text-[11px] text-slate-400">par {i.vendor_name}</span>
                  </span>
                  <span className="font-bold text-ink">{formatPrice(i.line_total)}</span>
                </div>
              ))}
              <div className="mt-2 space-y-1 border-t border-line pt-2 text-xs text-slate-500">
                <div className="flex justify-between"><span>Sous-total</span><span>{formatPrice(detail.subtotal)}</span></div>
                {detail.discount > 0 && <div className="flex justify-between text-emerald-600"><span>Remise {detail.promo_code ? `(${detail.promo_code})` : ""}</span><span>−{formatPrice(detail.discount)}</span></div>}
                <div className="flex justify-between text-sm font-black text-ink"><span>Total</span><span className="text-brand">{formatPrice(detail.total)}</span></div>
              </div>
            </div>

            <div className="rounded-xl border border-line p-4" data-testid="vsale-payment">
              <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">Paiement</p>
              {detail.payment_claim ? (
                <div className="space-y-1 text-sm">
                  <p>Moyen : <span className="font-bold text-ink">{detail.payment_claim.method_name}</span></p>
                  <p>Référence / TXID : <span className="font-mono font-bold text-ink">{detail.payment_claim.transaction_id || "—"}</span></p>
                  <p>Statut : <span className="font-bold text-ink">{detail.payment_claim.status}</span>
                    {detail.payment_claim.verified_by && <span className="text-xs text-slate-400"> · vérifié par {detail.payment_claim.verified_by}</span>}
                  </p>
                  {proofUrl && (
                    <a href={proofUrl} target="_blank" rel="noreferrer" data-testid="vsale-proof"
                      className="inline-flex items-center gap-1 text-xs font-bold text-brand"><FileText className="h-3.5 w-3.5" /> Voir la preuve de paiement</a>
                  )}
                </div>
              ) : <p className="text-sm text-slate-500">Aucune déclaration de paiement.</p>}
            </div>

            <div className="rounded-xl border border-line p-4">
              <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">Historique de la commande & du paiement</p>
              <div className="max-h-60 space-y-2 overflow-y-auto" data-testid="vsale-history">
                {(detail.status_history || []).slice().reverse().map((h, idx) => (
                  <div key={idx} className="border-l-2 border-brand/30 pl-3 text-xs">
                    <p className="font-bold text-ink">{h.label}</p>
                    {h.note && <p className="text-slate-500">{h.note}</p>}
                    <p className="text-slate-400">{formatDateTime(h.at)}{h.by ? ` · ${h.by}` : ""}</p>
                  </div>
                ))}
                {!(detail.status_history || []).length && <p className="text-xs text-slate-400">Aucun historique.</p>}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 border-t border-line pt-4">
              <a href={`/admin/revendeurs`} className="flex items-center gap-1.5 rounded-full border border-line px-4 py-2 text-xs font-bold text-ink hover:border-brand">
                <AlertTriangle className="h-3.5 w-3.5" /> Gérer le revendeur (sanctions)
              </a>
            </div>
          </div>
        )}
      </AdminModal>
    </div>
  );
}
